/**
 * NeoCore · Reportes de cartera
 *
 * - Pasiva (obligaciones con asociados): Ahorros + Plazos Fijos + Aportaciones
 * - Activa (créditos): consolidado por estado + detalle por asociado
 *
 * Task #144 · sesión Carlitos.
 */

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Printer, TrendUp, TrendDown, FilePdf, MicrosoftExcelLogo } from '@phosphor-icons/react'
import { exportarPDF, exportarExcel } from '@/lib/exportReport'
import { fetchConfigReporte } from '@/lib/useConfigReporte'
import { hoyGT } from '../lib/fechas'

const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

type Pasiva = {
  producto: string
  cantidad_cuentas: number
  saldo_total: number
  saldo_promedio: number
  detalle: any
}
type PasivaDetalle = {
  cuenta_id: string
  producto: string
  numero_cuenta: string
  asociado_id: string
  asociado_nombre: string
  neo_id: string
  saldo: number
  estado: string
  tasa: number | null
  plazo_dias: number | null
  fecha_apertura: string
  fecha_vencimiento: string | null
}
type Activa = {
  estado_credito: string
  cantidad_creditos: number
  capital_inicial_total: number
  saldo_capital_total: number
  intereses_devengados: number
  mora_acumulada: number
  detalle: any
}
type ActivaDetalle = {
  credito_id: string
  codigo: string
  asociado_nombre: string
  neo_id: string
  estado: string
  tipo_credito: string
  capital_inicial: number
  saldo_capital: number
  saldo_vigente: number
  saldo_en_mora: number
  intereses_devengados: number
  intereses_cobrados: number
  mora_acumulada: number
  tasa: number
  periodicidad: string
  plazo_dias: number
  numero_cuotas: number
  fecha_desembolso: string
  fecha_vencimiento: string
  fecha_ultima_cuota: string
  cuotas_pendientes: number
  dias_mora_max: number
  saldo_contable: number
}

export function NeoCoreCarterasPage() {
  const [tab, setTab] = useState<'pasiva' | 'activa'>('pasiva')

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 500 }}>Reportes de cartera</h1>
        <p style={{ marginTop: 4, color: 'var(--muted-foreground)', fontSize: 13 }}>
          Consolidado y detalle de cartera pasiva (ahorros/PF/aportaciones) y activa (créditos).
        </p>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        <TabBtn active={tab === 'pasiva'} onClick={() => setTab('pasiva')} icon={TrendDown}>
          Cartera Pasiva
        </TabBtn>
        <TabBtn active={tab === 'activa'} onClick={() => setTab('activa')} icon={TrendUp}>
          Cartera Activa (Créditos)
        </TabBtn>
      </div>

      {tab === 'pasiva' ? <PasivaView /> : <ActivaView />}
    </div>
  )
}

function TabBtn({ active, onClick, icon: Icon, children }: any) {
  return (
    <button onClick={onClick} style={{
      padding: '10px 14px', border: 'none', background: 'transparent',
      borderBottom: active ? '2px solid var(--brand-600, #FF5B04)' : '2px solid transparent',
      color: active ? 'var(--brand-600, #FF5B04)' : 'var(--ink-soft)',
      fontWeight: active ? 500 : 400, fontSize: 13, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', gap: 6,
    }}>
      <Icon size={15} /> {children}
    </button>
  )
}

// ═══════════════════════════════════════════════════════════════════
// Pasiva
// ═══════════════════════════════════════════════════════════════════

function PasivaView() {
  const [rows, setRows] = useState<Pasiva[]>([])
  const [detalle, setDetalle] = useState<PasivaDetalle[]>([])
  const [productoFiltro, setProductoFiltro] = useState('')
  const [fechaDesde, setFechaDesde] = useState('')
  const [fechaHasta, setFechaHasta] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [loading, setLoading] = useState(true)

  // Query aplicada: solo cambia al presionar "Aplicar filtros" (no en onChange,
  // así el date picker no se interrumpe).
  const [query, setQuery] = useState({ producto: '', desde: '', hasta: '', busqueda: '' })

  useEffect(() => {
    Promise.all([
      supabase.rpc('reporte_cartera_pasiva', { p_fecha_corte: query.hasta || hoyGT() }),
      supabase.rpc('reporte_cartera_pasiva_detalle', {
        p_producto: query.producto || null,
        p_fecha_desde: query.desde || null,
        p_fecha_hasta: query.hasta || null,
        p_busqueda: query.busqueda || null,
      }),
    ]).then(([{ data: c }, { data: d }]) => {
      setRows((c ?? []) as Pasiva[])
      setDetalle((d ?? []) as PasivaDetalle[])
      setLoading(false)
    })
  }, [query])

  const aplicar = () => setQuery({ producto: productoFiltro, desde: fechaDesde, hasta: fechaHasta, busqueda })
  const filtrosSinAplicar =
    productoFiltro !== query.producto || fechaDesde !== query.desde || fechaHasta !== query.hasta || busqueda !== query.busqueda
  const filtrosActivos = !!(productoFiltro || fechaDesde || fechaHasta || busqueda)
  const limpiarFiltros = () => {
    setProductoFiltro(''); setFechaDesde(''); setFechaHasta(''); setBusqueda('')
    setQuery({ producto: '', desde: '', hasta: '', busqueda: '' })
  }

  const totalCuentas = rows.reduce((s, r) => s + Number(r.cantidad_cuentas), 0)
  const totalSaldo = rows.reduce((s, r) => s + Number(r.saldo_total), 0)
  const totalDetalle = detalle.reduce((s, r) => s + Number(r.saldo), 0)

  const exportar = async (formato: 'pdf' | 'excel') => {
    const config = await fetchConfigReporte()
    const headers = ['Producto', 'Cuentas', 'Saldo total', 'Saldo promedio']
    const filas: (string | number)[][] = rows.map(r => [
      r.producto,
      r.cantidad_cuentas,
      `Q ${fmt(r.saldo_total)}`,
      `Q ${fmt(r.saldo_promedio)}`,
    ])
    const totalRow = ['TOTAL', totalCuentas, `Q ${fmt(totalSaldo)}`, '']
    const fn = formato === 'pdf' ? exportarPDF : exportarExcel
    await fn({
      config,
      titulo: 'CARTERA PASIVA',
      subtitulo: `Corte al ${hoyGT()}`,
      headers, rows: filas, totalRow,
      orientacion: 'portrait',
      columnasNumericas: [1, 2, 3],
      filename: 'cartera-pasiva',
    })
  }

  const exportarDetalle = async (formato: 'pdf' | 'excel') => {
    const config = await fetchConfigReporte()
    const headers = ['Producto', 'Cuenta', 'Asociado', 'neoID', 'Saldo', 'Estado', 'Tasa', 'Plazo', 'Apertura', 'Vencimiento']
    const filas: (string | number)[][] = detalle.map(r => [
      r.producto, r.numero_cuenta, r.asociado_nombre, r.neo_id ?? '',
      `Q ${fmt(Number(r.saldo))}`, r.estado,
      r.tasa != null ? `${fmt(Number(r.tasa))}%` : '',
      r.plazo_dias != null ? `${r.plazo_dias}d` : '',
      r.fecha_apertura ?? '', r.fecha_vencimiento ?? '',
    ])
    const totalRow = ['TOTAL', '', '', '', `Q ${fmt(totalDetalle)}`, '', '', '', '', '']
    const fn = formato === 'pdf' ? exportarPDF : exportarExcel
    await fn({
      config,
      titulo: 'CARTERA PASIVA · DETALLE POR ASOCIADO',
      subtitulo: `Corte al ${hoyGT()}`,
      headers, rows: filas, totalRow,
      orientacion: 'landscape',
      columnasNumericas: [4],
      filename: 'cartera-pasiva-detalle',
    })
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi label="Total cuentas activas" value={totalCuentas.toString()} />
        <Kpi label="Saldo total pasivo" value={`Q ${fmt(totalSaldo)}`} big />
        <Kpi label="Productos" value={rows.length.toString()} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button onClick={() => exportar('pdf')} style={btnExport}>
            <FilePdf size={14} /> PDF
          </button>
          <button onClick={() => exportar('excel')} style={btnExport}>
            <MicrosoftExcelLogo size={14} /> Excel
          </button>
          <button onClick={() => window.print()} style={btnExport}>
            <Printer size={14} /> Imprimir
          </button>
        </div>
      </div>

      <div className="report-print-area" style={{ background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: 'var(--n-50, #f4f4f5)', borderBottom: '1px solid var(--border)' }}>
            <tr>
              <th style={th}>Producto</th>
              <th style={{ ...th, textAlign: 'right' }}>Cuentas</th>
              <th style={{ ...th, textAlign: 'right' }}>Saldo total</th>
              <th style={{ ...th, textAlign: 'right' }}>Saldo promedio</th>
              <th style={th}>Detalle</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.producto} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ ...td, fontWeight: 500 }}>{r.producto}</td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.cantidad_cuentas}</td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                  Q {fmt(r.saldo_total)}
                </td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--muted-foreground)' }}>
                  Q {fmt(r.saldo_promedio)}
                </td>
                <td style={{ ...td, fontSize: 11, color: 'var(--muted-foreground)' }}>
                  {r.detalle && Object.entries(r.detalle).map(([k, v]) => (
                    <span key={k} style={{ marginRight: 12 }}>
                      <b>{k}:</b> {String(v)}
                    </span>
                  ))}
                </td>
              </tr>
            ))}
            <tr style={{ background: '#f4f4f5', fontWeight: 500 }}>
              <td style={td}>TOTAL</td>
              <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{totalCuentas}</td>
              <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>Q {fmt(totalSaldo)}</td>
              <td colSpan={2}></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Detalle por asociado */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 28, marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>Detalle por asociado</h3>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
            {detalle.length} cuenta{detalle.length === 1 ? '' : 's'} · Q {fmt(totalDetalle)}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => exportarDetalle('pdf')} style={btnExport}><FilePdf size={14} /> PDF</button>
          <button onClick={() => exportarDetalle('excel')} style={btnExport}><MicrosoftExcelLogo size={14} /> Excel</button>
        </div>
      </div>

      {/* Barra de filtros */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', marginBottom: 8 }}>
        <label style={filtroLabel}>Producto
          <select value={productoFiltro} onChange={(e) => setProductoFiltro(e.target.value)} style={filtroInput}>
            <option value="">Todos</option>
            <option value="ahorro">Ahorro corriente</option>
            <option value="pf">Plazo fijo (CDP)</option>
            <option value="aportaciones">Aportaciones</option>
          </select>
        </label>
        <label style={filtroLabel}>Desde
          <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} style={filtroInput} />
        </label>
        <label style={filtroLabel}>Hasta
          <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} style={filtroInput} />
        </label>
        <label style={{ ...filtroLabel, flex: 1, minWidth: 180 }}>Buscar
          <input type="text" value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') aplicar() }}
            placeholder="Nombre, neoID o cuenta…" style={filtroInput} />
        </label>
        <button onClick={aplicar} disabled={!filtrosSinAplicar} style={{
          padding: '6px 12px', border: 'none', borderRadius: 4, height: 32, cursor: filtrosSinAplicar ? 'pointer' : 'default',
          background: filtrosSinAplicar ? 'var(--brand-600, #FF5B04)' : 'var(--n-200, #e5e5e5)',
          color: filtrosSinAplicar ? '#fff' : 'var(--muted-foreground)', fontSize: 12, fontWeight: 500,
        }}>Aplicar filtros</button>
        {filtrosActivos && (
          <button onClick={limpiarFiltros} style={{
            padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 4,
            background: 'transparent', fontSize: 12, cursor: 'pointer', height: 32,
          }}>Limpiar</button>
        )}
      </div>

      <div style={{ background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ background: 'var(--n-50, #f4f4f5)' }}>
            <tr>
              <th style={th}>Producto</th>
              <th style={th}>Cuenta</th>
              <th style={th}>Asociado</th>
              <th style={th}>neoID</th>
              <th style={{ ...th, textAlign: 'right' }}>Saldo</th>
              <th style={th}>Estado</th>
              <th style={{ ...th, textAlign: 'right' }}>Tasa</th>
              <th style={{ ...th, textAlign: 'right' }}>Plazo</th>
              <th style={th}>Apertura</th>
              <th style={th}>Vencimiento</th>
            </tr>
          </thead>
          <tbody>
            {detalle.length === 0 ? (
              <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: 'var(--muted-foreground)' }}>Sin cuentas</td></tr>
            ) : detalle.map((r) => (
              <tr key={r.cuenta_id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ ...td, fontSize: 11, color: 'var(--muted-foreground)' }}>{r.producto}</td>
                <td style={{ ...td, fontFamily: 'monospace' }}>{r.numero_cuenta}</td>
                <td style={td}>{r.asociado_nombre}</td>
                <td style={{ ...td, fontFamily: 'monospace', color: 'var(--muted-foreground)' }}>{r.neo_id}</td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>Q {fmt(Number(r.saldo))}</td>
                <td style={{ ...td, fontSize: 11 }}>{r.estado}</td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.tasa != null ? `${fmt(Number(r.tasa))}%` : '—'}</td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.plazo_dias != null ? `${r.plazo_dias}d` : '—'}</td>
                <td style={{ ...td, fontSize: 11 }}>{r.fecha_apertura ?? '—'}</td>
                <td style={{ ...td, fontSize: 11 }}>{r.fecha_vencimiento ?? '—'}</td>
              </tr>
            ))}
            {detalle.length > 0 && (
              <tr style={{ background: '#f4f4f5', fontWeight: 500 }}>
                <td style={td} colSpan={4}>TOTAL</td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>Q {fmt(totalDetalle)}</td>
                <td colSpan={5}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════
// Activa
// ═══════════════════════════════════════════════════════════════════

function ActivaView() {
  const [consolidado, setConsolidado] = useState<Activa[]>([])
  const [detalle, setDetalle] = useState<ActivaDetalle[]>([])
  const [estadoFiltro, setEstadoFiltro] = useState('')
  const [fechaDesde, setFechaDesde] = useState('')
  const [fechaHasta, setFechaHasta] = useState('')
  const [tipoFiltro, setTipoFiltro] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [loading, setLoading] = useState(true)

  // Query aplicada: fechas/tipo/búsqueda cambian solo al presionar "Aplicar"
  // (no en onChange). El estado es drill-down: se aplica al instante al hacer clic.
  const [query, setQuery] = useState({ estado: '', desde: '', hasta: '', tipo: '', busqueda: '' })
  const [riesgo, setRiesgo] = useState<{ categoria: string; creditos: number; saldo: number; pct: number }[]>([])

  // Clasificación de riesgo (días calendario): al día · atraso 1–14 · pre-mora 15–30 · mora 31+ (0392)
  useEffect(() => {
    supabase.rpc('cartera_riesgo_resumen').then(({ data }) => setRiesgo((data ?? []) as typeof riesgo))
  }, [])

  useEffect(() => {
    Promise.all([
      supabase.rpc('reporte_cartera_activa'),
      supabase.rpc('reporte_cartera_activa_detalle', {
        p_estado_filter: query.estado || null,
        p_fecha_desde: query.desde || null,
        p_fecha_hasta: query.hasta || null,
        p_tipo: query.tipo || null,
        p_busqueda: query.busqueda || null,
      }),
    ]).then(([{ data: c }, { data: d }]) => {
      setConsolidado((c ?? []) as Activa[])
      setDetalle((d ?? []) as ActivaDetalle[])
      setLoading(false)
    })
  }, [query])

  const aplicar = () => setQuery({ estado: estadoFiltro, desde: fechaDesde, hasta: fechaHasta, tipo: tipoFiltro, busqueda })
  // Drill-down por estado: setea draft + aplica al instante
  const setEstado = (v: string) => { setEstadoFiltro(v); setQuery((q) => ({ ...q, estado: v })) }
  const filtrosSinAplicar =
    fechaDesde !== query.desde || fechaHasta !== query.hasta || tipoFiltro !== query.tipo || busqueda !== query.busqueda
  const filtrosActivos = !!(fechaDesde || fechaHasta || tipoFiltro || busqueda || estadoFiltro)
  const limpiarFiltros = () => {
    setEstadoFiltro(''); setFechaDesde(''); setFechaHasta(''); setTipoFiltro(''); setBusqueda('')
    setQuery({ estado: '', desde: '', hasta: '', tipo: '', busqueda: '' })
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>

  const totalCreditos = consolidado.reduce((s, r) => s + Number(r.cantidad_creditos), 0)
  const totalSaldo = consolidado.reduce((s, r) => s + Number(r.saldo_capital_total), 0)
  const totalMora = consolidado.reduce((s, r) => s + Number(r.mora_acumulada), 0)

  const exportar = async (formato: 'pdf' | 'excel', vista: 'consolidado' | 'detalle') => {
    const config = await fetchConfigReporte()
    let headers: string[], filas: (string | number)[][], totalRow: (string | number)[] | undefined, titulo: string, filename: string

    if (vista === 'consolidado') {
      headers = ['Estado', 'Cantidad', 'Capital inicial', 'Saldo capital', 'Intereses pend.', 'Mora']
      filas = consolidado.map(r => [
        r.estado_credito,
        r.cantidad_creditos,
        `Q ${fmt(r.capital_inicial_total)}`,
        `Q ${fmt(r.saldo_capital_total)}`,
        `Q ${fmt(r.intereses_devengados)}`,
        `Q ${fmt(r.mora_acumulada)}`,
      ])
      totalRow = ['TOTAL', totalCreditos, '', `Q ${fmt(totalSaldo)}`, '', `Q ${fmt(totalMora)}`]
      titulo = 'CARTERA ACTIVA · CONSOLIDADO'
      filename = 'cartera-activa-consolidado'
    } else {
      headers = ['Código', 'Asociado', 'neoID', 'Tipo', 'Estado', 'Capital', 'Saldo', 'Saldo contable', 'Saldo vig.', 'Saldo mora', 'Int. dev.', 'Mora', 'Tasa', 'Periodicidad', 'Plazo', 'N° cuotas', 'Desembolso', 'Vencimiento', 'Cuotas pend', 'Días mora']
      filas = detalle.map(r => [
        r.codigo, r.asociado_nombre, r.neo_id, r.tipo_credito, r.estado,
        `Q ${fmt(r.capital_inicial)}`,
        `Q ${fmt(r.saldo_capital)}`,
        `Q ${fmt(r.saldo_contable)}`,
        `Q ${fmt(r.saldo_vigente)}`,
        `Q ${fmt(r.saldo_en_mora)}`,
        `Q ${fmt(r.intereses_devengados - r.intereses_cobrados)}`,
        `Q ${fmt(r.mora_acumulada)}`,
        `${(r.tasa * 100).toFixed(1)}%`,
        r.periodicidad,
        `${r.plazo_dias}d`,
        r.numero_cuotas,
        r.fecha_desembolso,
        r.fecha_vencimiento,
        r.cuotas_pendientes,
        r.dias_mora_max,
      ])
      const sum = (k: 'capital_inicial' | 'saldo_capital' | 'saldo_contable') => detalle.reduce((s, r) => s + Number(r[k]), 0)
      totalRow = ['TOTAL', `${detalle.length} créditos`, '', '', '', `Q ${fmt(sum('capital_inicial'))}`, `Q ${fmt(sum('saldo_capital'))}`, `Q ${fmt(sum('saldo_contable'))}`, '', '', '', '', '', '', '', '', '', '', '', '']
      titulo = `CARTERA ACTIVA · DETALLE${estadoFiltro ? ` (${estadoFiltro})` : ''}`
      filename = `cartera-activa-detalle${estadoFiltro ? `-${estadoFiltro}` : ''}`
    }

    const fn = formato === 'pdf' ? exportarPDF : exportarExcel
    await fn({
      config, titulo,
      subtitulo: `Al ${vista === 'detalle' && query.hasta ? query.hasta : hoyGT()}`,
      headers, rows: filas, totalRow,
      orientacion: 'landscape',
      columnasNumericas: vista === 'consolidado' ? [1, 2, 3, 4, 5] : [5, 6, 7, 8, 9, 10, 11],
      filename,
    })
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi label="Total créditos" value={totalCreditos.toString()} />
        <Kpi label="Saldo cartera" value={`Q ${fmt(totalSaldo)}`} big />
        <Kpi label="Mora acumulada" value={`Q ${fmt(totalMora)}`} accent={totalMora > 0 ? '#EF4444' : undefined} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button onClick={() => exportar('pdf', 'consolidado')} style={btnExport}>
            <FilePdf size={14} /> PDF consolidado
          </button>
          <button onClick={() => exportar('excel', 'consolidado')} style={btnExport}>
            <MicrosoftExcelLogo size={14} /> Excel consolidado
          </button>
          <button onClick={() => window.print()} style={btnExport}>
            <Printer size={14} /> Imprimir
          </button>
        </div>
      </div>

      {/* Riesgo de cartera (reporte) */}
      {riesgo.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 13, fontWeight: 500, color: 'var(--muted-foreground)', textTransform: 'uppercase',
            letterSpacing: 0.5, marginBottom: 8 }}>Riesgo de cartera · días calendario de atraso</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
            {riesgo.map((r) => {
              const meta: Record<string, { l: string; sub: string; c: string }> = {
                al_dia: { l: 'Al día', sub: '0 días', c: '#15803D' },
                atraso: { l: 'Atraso', sub: '1–14 días', c: '#CA8A04' },
                pre_mora: { l: 'Pre-mora', sub: '15–30 días', c: '#EA580C' },
                mora: { l: 'Mora', sub: '31+ días', c: '#DC2626' },
              }
              const m = meta[r.categoria] ?? { l: r.categoria, sub: '', c: 'var(--ink)' }
              return (
                <div key={r.categoria} style={{ padding: 14, background: 'var(--surface, #fff)', border: '1px solid var(--border)',
                  borderRadius: 12, borderLeft: `3px solid ${m.c}` }}>
                  <div style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
                    {m.l} · {m.sub}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 600, color: m.c, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{Number(r.pct).toFixed(1)}%</div>
                  <div style={{ fontSize: 12, color: 'var(--muted-foreground)', fontVariantNumeric: 'tabular-nums' }}>
                    {r.creditos} créditos · Q {fmt(Number(r.saldo))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Consolidado */}
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 13, fontWeight: 500, color: 'var(--muted-foreground)', textTransform: 'uppercase',
          letterSpacing: 0.5, marginBottom: 8 }}>Consolidado por estado</h3>
        <div style={{ background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: 'var(--n-50, #f4f4f5)' }}>
              <tr>
                <th style={th}>Estado</th>
                <th style={{ ...th, textAlign: 'right' }}>Cantidad</th>
                <th style={{ ...th, textAlign: 'right' }}>Capital inicial</th>
                <th style={{ ...th, textAlign: 'right' }}>Saldo capital</th>
                <th style={{ ...th, textAlign: 'right' }}>Intereses pendientes</th>
                <th style={{ ...th, textAlign: 'right' }}>Mora</th>
                <th style={th}>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {consolidado.map((r) => (
                <tr
                  key={r.estado_credito}
                  onClick={() => setEstado(estadoFiltro === r.estado_credito ? '' : r.estado_credito)}
                  style={{
                    borderBottom: '1px solid var(--border)', cursor: 'pointer',
                    background: estadoFiltro === r.estado_credito ? 'var(--brand-50, #fff5ed)' : undefined,
                  }}
                >
                  <td style={{ ...td, textTransform: 'uppercase', fontWeight: 500 }}>{r.estado_credito}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.cantidad_creditos}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>Q {fmt(r.capital_inicial_total)}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>Q {fmt(r.saldo_capital_total)}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>Q {fmt(r.intereses_devengados)}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.mora_acumulada > 0 ? '#EF4444' : undefined }}>
                    Q {fmt(r.mora_acumulada)}
                  </td>
                  <td style={{ ...td, fontSize: 11, color: 'var(--muted-foreground)' }}>
                    Tasa prom {r.detalle?.tasa_promedio}% · plazo {r.detalle?.plazo_promedio}d
                  </td>
                </tr>
              ))}
              {consolidado.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--muted-foreground)' }}>
                  Sin créditos registrados
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detalle por asociado */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <h3 style={{ fontSize: 13, fontWeight: 500, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, margin: 0 }}>
            Detalle por asociado {estadoFiltro && <span style={{ color: 'var(--brand-600)' }}>· filtro: {estadoFiltro}</span>}
          </h3>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => exportar('pdf', 'detalle')} style={btnMini} title="Exportar detalle PDF">
              <FilePdf size={11} /> PDF
            </button>
            <button onClick={() => exportar('excel', 'detalle')} style={btnMini} title="Exportar detalle Excel">
              <MicrosoftExcelLogo size={11} /> Excel
            </button>
            {estadoFiltro && (
              <button onClick={() => setEstado('')} style={{
                padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 4,
                background: 'transparent', fontSize: 11, cursor: 'pointer',
              }}>Quitar filtro</button>
            )}
          </div>
        </div>
        {/* Barra de filtros */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', marginBottom: 8 }}>
          <label style={filtroLabel}>Desde
            <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} style={filtroInput} />
          </label>
          <label style={filtroLabel}>Hasta
            <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} style={filtroInput} />
          </label>
          <label style={filtroLabel}>Tipo
            <select value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)} style={filtroInput}>
              <option value="">Todos</option>
              <option value="emergente_quincenal">Emergente</option>
              <option value="consumo">Consumo</option>
            </select>
          </label>
          <label style={{ ...filtroLabel, flex: 1, minWidth: 180 }}>Buscar
            <input type="text" value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') aplicar() }}
              placeholder="Nombre, neoID o código…" style={filtroInput} />
          </label>
          <button onClick={aplicar} disabled={!filtrosSinAplicar} style={{
            padding: '6px 12px', border: 'none', borderRadius: 4, height: 32, cursor: filtrosSinAplicar ? 'pointer' : 'default',
            background: filtrosSinAplicar ? 'var(--brand-600, #FF5B04)' : 'var(--n-200, #e5e5e5)',
            color: filtrosSinAplicar ? '#fff' : 'var(--muted-foreground)', fontSize: 12, fontWeight: 500,
          }}>Aplicar filtros</button>
          {filtrosActivos && (
            <button onClick={limpiarFiltros} style={{
              padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 4,
              background: 'transparent', fontSize: 12, cursor: 'pointer', height: 32,
            }}>Limpiar</button>
          )}
        </div>
        <div style={{ background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ background: 'var(--n-50, #f4f4f5)' }}>
              <tr>
                <th style={th}>Código</th>
                <th style={th}>Asociado</th>
                <th style={th}>neoID</th>
                <th style={th}>Tipo</th>
                <th style={th}>Estado</th>
                <th style={{ ...th, textAlign: 'right' }}>Capital</th>
                <th style={{ ...th, textAlign: 'right' }}>Saldo{query.hasta ? ` al ${query.hasta}` : ''}</th>
                <th style={{ ...th, textAlign: 'right' }} title="Cuenta 10310105* (cartera vigente) del libro, asientos vigentes hasta la fecha de corte">Saldo contable</th>
                <th style={{ ...th, textAlign: 'right' }}>Saldo vig.</th>
                <th style={{ ...th, textAlign: 'right' }}>Saldo mora</th>
                <th style={{ ...th, textAlign: 'right' }}>Int. dev.</th>
                <th style={{ ...th, textAlign: 'right' }}>Mora</th>
                <th style={{ ...th, textAlign: 'right' }}>Tasa</th>
                <th style={th}>Periodicidad</th>
                <th style={{ ...th, textAlign: 'right' }}>Plazo</th>
                <th style={{ ...th, textAlign: 'right' }}>N° cuotas</th>
                <th style={th}>Desembolso</th>
                <th style={th}>Vencimiento</th>
                <th style={{ ...th, textAlign: 'right' }}>Cuotas pend</th>
                <th style={{ ...th, textAlign: 'right' }}>Días mora</th>
              </tr>
            </thead>
            <tbody>
              {detalle.length === 0 && (
                <tr><td colSpan={20} style={{ padding: 24, textAlign: 'center', color: 'var(--muted-foreground)' }}>Sin créditos</td></tr>
              )}
              {detalle.map((r) => (
                <tr key={r.credito_id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, fontWeight: 600 }}>{r.codigo}</td>
                  <td style={td}>{r.asociado_nombre}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{r.neo_id}</td>
                  <td style={{ ...td, fontSize: 11 }}>{r.tipo_credito}</td>
                  <td style={{ ...td, fontSize: 11, textTransform: 'uppercase' }}>{r.estado}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>Q {fmt(r.capital_inicial)}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>Q {fmt(r.saldo_capital)}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: Math.abs(Number(r.saldo_contable) - Number(r.saldo_capital)) > 0.005 ? '#B45309' : undefined }}>Q {fmt(Number(r.saldo_contable))}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>Q {fmt(r.saldo_vigente)}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.saldo_en_mora > 0 ? '#EF4444' : undefined }}>Q {fmt(r.saldo_en_mora)}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>Q {fmt(r.intereses_devengados - r.intereses_cobrados)}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.mora_acumulada > 0 ? '#EF4444' : undefined }}>
                    Q {fmt(r.mora_acumulada)}
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{(r.tasa * 100).toFixed(1)}%</td>
                  <td style={{ ...td, fontSize: 11, textTransform: 'capitalize' }}>{r.periodicidad}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.plazo_dias}d</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.numero_cuotas}</td>
                  <td style={td}>{r.fecha_desembolso}</td>
                  <td style={td}>{r.fecha_vencimiento}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.cuotas_pendientes}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.dias_mora_max > 0 ? '#EF4444' : undefined }}>
                    {r.dias_mora_max}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function Kpi({ label, value, big, accent }: { label: string; value: string; big?: boolean; accent?: string }) {
  return (
    <div style={{
      padding: 16, background: 'var(--surface, #fff)',
      border: '1px solid var(--border)', borderRadius: 12,
      borderLeft: accent ? `3px solid ${accent}` : undefined,
    }}>
      <div style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
        {label}
      </div>
      <div style={{
        fontSize: big ? 22 : 18, fontWeight: 500, marginTop: 6,
        fontVariantNumeric: 'tabular-nums',
        color: accent || 'var(--ink)',
      }}>{value}</div>
    </div>
  )
}

const th: React.CSSProperties = {
  padding: '8px 10px', textAlign: 'left', fontSize: 11,
  color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500,
}
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'middle' }
const filtroLabel: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11,
  color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 500,
}
const filtroInput: React.CSSProperties = {
  padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4,
  background: 'var(--surface)', fontSize: 13, color: 'var(--ink, inherit)', height: 32,
  textTransform: 'none', letterSpacing: 0, fontWeight: 400,
}
const btnExport: React.CSSProperties = {
  padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 6,
  background: 'var(--surface)', fontSize: 12, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
}
const btnMini: React.CSSProperties = {
  padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 4,
  background: 'transparent', fontSize: 11, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 3,
}
